/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useMemo} from 'react'
import Cookies from 'js-cookie'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {useAccessToken} from '@salesforce/commerce-sdk-react'

// Chakra
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'
import useOcapiSite from '../../hooks/useOcapiSite'

// Contexts
import {CurrencyProvider, HeaderProvider} from '../../contexts'

// Local Project Components
import {useGeoIPModal, CountrySelectorModal} from '../country-selector-modal'
import MessageBar from '../../leap/components/message-bar'
import LeapHeader from '../../leap/components/navigation/header'
import OfflineBanner from '../../components/offline-banner'
import OfflineBoundary from '../../components/offline-boundary'
import ScrollToTop from '../../components/scroll-to-top'

const Footer = lazyLoadable(() => import('../../leap/components/footer'), {
    loadOnIdle: true
})
const CheckoutFooter = lazyLoadable(() => import('../../leap/components/checkout-footer'), {
    loadOnIdle: true
})
import CMSGlobalStyles from '../cms-content/global-styles'

// Hooks
import useShopper from '../../commerce-api/hooks/useShopper'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {DntNotification, useDntNotification} from '../../hooks/use-dnt-notification'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import {useSapiGeoIpData} from '../../hooks/useSapi'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'
// Localization
import {IntlProvider} from 'react-intl'

// Others
import {isServer, watchOnlineStatus} from '../../utils/utils'

import {
    DEFAULT_SITE_TITLE,
    HOME_HREF,
    CART_HREF,
    THEME_COLOR,
    PWA_DNT_NOTIFICATION_ENABLED,
    CHECKOUT_HREF,
    REGISTRATION_HREF
} from '../../constants'

import Seo from '../seo'
import CookieBot from '../cookiebot'
import Gtm from '../../gtm-api/gtm'
import useMultiSite from '../../hooks/use-multi-site'
import FooterNewsletter from '../../leap/components/footer-newsletter'
import {ConvertAPIProvider} from '../../convert-api'
import {ExposedProductQuickviewModal} from '../product-quickview-modal'
import {Toaster} from '../toaster'
import useEffectEvent from '../../hooks/use-effect-event'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useIntlConfig from './hooks/useIntlConfig'
import useDisableGtm from '../../hooks/use-disable-gtm'
import {detectStorefrontPreview} from '@salesforce/commerce-sdk-react/components/StorefrontPreview/utils'
import lazyLoadable from '../../utils/lazy-loadable'
import {ZendeskChatProvider} from '../zendesk-chat'
import {
    ProductConfiguratorProvider,
    useProductConfigurator
} from '../../contexts/product-configurator'
import {LeapOverlayProvider} from '../../leap/hooks/useOverlay'
import {OverlayProvider, OverlayViewport} from '../../contexts/overlay-manager'
import {Chakra3Theme} from '../../leap/theme'
import {NavigationProvider} from '../../leap/components/navigation/navigation-provider'
import CheckoutHeader from '../../leap/components/navigation/checkout-header'

const App = (props) => {
    const {children} = props
    const {app: appConfig} = getConfig()
    // Set up ocapi site
    const {
        croConvertEnabled,
        croConvertScriptUrl,
        GoogleVerificationTag: googleVerificationTag
    } = useOcapiSite().preferences
    const headerRef = useRef()
    const mainContentRef = useRef(null)
    const location = useLocation()
    const authModal = useAuthModal()
    const dntNotification = useDntNotification()
    const {site, locale} = useMultiSite()
    const countryAndLocaleCookie = Cookies.get('dw_country_and_locale')
    const {onOpen: onGeoIpModal, ...geoIpModalProps} = useGeoIPModal()

    const [isOnline, setIsOnline] = useState(true)
    const styles = useMultiStyleConfig('App')

    const intlConfig = useIntlConfig()

    // Used to conditionally render header/footer for cancel order & checkout pages
    const {
        isCheckout,
        isCancelOrder,
        isCheckoutConfirmation,
        isCart,
        isHome,
        isRegistration,
        isNewsletterPage
    } = useMemo(() => {
        const isCheckout = new RegExp(`${CHECKOUT_HREF}$`).test(location?.pathname)
        const isCancelOrder = /\/cancel-order/.test(location?.pathname)
        const isCheckoutConfirmation = new RegExp(`${CHECKOUT_HREF}/confirmation$`).test(
            location?.pathname
        )
        const isCart = new RegExp(`${CART_HREF}$`).test(location?.pathname)
        const isHome = new RegExp(`${HOME_HREF}$`).test(location?.pathname)
        const isRegistration = new RegExp(`${REGISTRATION_HREF}$`).test(location?.pathname)
        const isNewsletterPage = /(\/content\/newsletter|\/ct\/newsletter.html)/.test(
            location?.pathname
        )

        return {
            isCheckout,
            isCancelOrder,
            isCheckoutConfirmation,
            isCart,
            isHome,
            isRegistration,
            isNewsletterPage
        }
    }, [location?.pathname])
    const isCheckOrConfirmationOrCancelOrder = isCheckout || isCheckoutConfirmation || isCancelOrder

    const productConfiguratorProps = useProductConfigurator()
    const {isConfiguratorActive} = productConfiguratorProps

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency
    const disableGtm = useDisableGtm()

    // fetch geoIp data
    const {
        data: geoIPData,
        isLoading: geoIpLoading,
        error: geoIpError
    } = useSapiGeoIpData({enabled: !countryAndLocaleCookie && !disableGtm && !isServer})

    const onShowGeoIpModal = useEffectEvent((geoIPData) => {
        onGeoIpModal(geoIPData)
    })
    useEffect(() => {
        if (
            !countryAndLocaleCookie &&
            !geoIpLoading &&
            !geoIpError &&
            geoIPData &&
            geoIPData.locale !== intlConfig.locale.replace('-', '_')
        ) {
            onShowGeoIpModal(geoIPData)
        }
    }, [
        countryAndLocaleCookie,
        geoIpLoading,
        geoIpError,
        geoIPData,
        onShowGeoIpModal,
        intlConfig.locale
    ])

    // Set up customer and basket
    useShopper({currency})

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    // fix preview mode as it iterates local storage keys to find out refresh token cookies
    // but the refresh tokens are only ever saved in cookies so we need to duplicate them
    // in local storage to make the preview mode clean up when the session expires
    useEffect(() => {
        if (detectStorefrontPreview()) {
            const t = /^(cc-nx-g-iframe|cc-nx-iframe)_*(.+)*/

            const onCookieChange = ({changed}) => {
                for (const {name, value} of changed) {
                    if (t.test(name)) {
                        global.localStorage.setItem(name, value)
                    }
                }
            }
            global.cookieStore?.addEventListener('change', onCookieChange)

            // duplicate the existing cookies
            global.cookieStore?.getAll().then((cookies) => {
                cookies.forEach((cookie) => {
                    if (t.test(cookie.name)) {
                        global.localStorage.setItem(cookie.name, cookie.value)
                    }
                })
            })

            return () => {
                global.cookieStore?.removeEventListener('change', onCookieChange)
            }
        }
    }, [])

    const onSkipLinkClick = () => {
        if (mainContentRef.current) {
            mainContentRef.current.focus()
        }
    }

    const {getTokenWhenReady} = useAccessToken()

    const showFooterNewsletter = !isRegistration && !isNewsletterPage && !isConfiguratorActive

    const header =
        !isConfiguratorActive &&
        (isCheckOrConfirmationOrCancelOrder ? (
            <CheckoutHeader isCheckoutConfirmation={isCheckoutConfirmation || isCancelOrder} />
        ) : (
            // The Xleap header owns its own `position: sticky` + pointer events (see navigation/header/index.recipe.js),
            // so it must NOT sit inside the legacy `headerWrapper` (fixed + pointer-events:none),
            // which would defeat both. The message bar and the header are direct children of
            // `#app` (no intermediate wrapper) so the header's `position: sticky` sticks across
            // the whole scroll container while the message bar scrolls away. `headerRef` attaches to the header itself,
            // so `useHeader().headerWrapper` resolves to `#app` (unchanged).
            <>
                <MessageBar />
                <LeapHeader ref={headerRef} onAccountClick={authModal.onOpen} />
            </>
        ))

    return (
        <ConvertAPIProvider convertScriptUrl={croConvertScriptUrl} disabled={!croConvertEnabled}>
            <Box className="sf-app" {...styles.container}>
                <StorefrontPreview getToken={getTokenWhenReady}>
                    <IntlProvider {...intlConfig}>
                        <CurrencyProvider currency={currency}>
                            <HeaderProvider header={headerRef}>
                                <Seo
                                    noIndex={appConfig.seo?.noIndex}
                                    htmlAttributes={{lang: locale.id}}
                                >
                                    <meta name="theme-color" content={THEME_COLOR} />
                                    <meta
                                        name="apple-mobile-web-app-title"
                                        content={DEFAULT_SITE_TITLE}
                                    />
                                    {isHome && googleVerificationTag && (
                                        <meta
                                            name="google-site-verification"
                                            content={googleVerificationTag}
                                        />
                                    )}
                                    <link
                                        rel="apple-touch-icon"
                                        href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                                    />
                                    <link
                                        rel="icon"
                                        type="image/png"
                                        sizes="32x32"
                                        href={getAssetUrl('static/img/global/favicon-32x32.png')}
                                    />
                                    <link
                                        rel="icon"
                                        type="image/png"
                                        sizes="16x16"
                                        href={getAssetUrl('static/img/global/favicon-16x16.png')}
                                    />
                                    <link
                                        rel="manifest"
                                        href={getAssetUrl('static/manifest.json')}
                                    />
                                    <link
                                        rel="mask-icon"
                                        href={getAssetUrl(
                                            'static/img/global/safari-pinned-tab.svg'
                                        )}
                                        color="#ED7703"
                                    />
                                </Seo>

                                <ScrollToTop />

                                <LeapOverlayProvider>
                                    <AddToCartModalProvider>
                                        <OverlayProvider>
                                            <NavigationProvider>
                                                <ZendeskChatProvider>
                                                    <Box
                                                        id="app"
                                                        display="flex"
                                                        flexDirection="column"
                                                        flex={1}
                                                    >
                                                        {!isConfiguratorActive && (
                                                            <SkipNavLink
                                                                zIndex="skipLink"
                                                                onClick={onSkipLinkClick}
                                                                tabIndex="0"
                                                            >
                                                                Skip to Content
                                                            </SkipNavLink>
                                                        )}

                                                        {header}

                                                        {!isOnline && <OfflineBanner />}
                                                        <SkipNavContent
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                flex: 1,
                                                                outline: 0
                                                            }}
                                                        >
                                                            <Box
                                                                as="main"
                                                                id="app-main"
                                                                ref={mainContentRef}
                                                                role="main"
                                                                display="flex"
                                                                flexDirection="column"
                                                                flex="1"
                                                                tabIndex="-1"
                                                            >
                                                                <OfflineBoundary isOnline={false}>
                                                                    <ProductConfiguratorProvider
                                                                        {...productConfiguratorProps}
                                                                    >
                                                                        {children}
                                                                    </ProductConfiguratorProvider>
                                                                </OfflineBoundary>
                                                            </Box>
                                                        </SkipNavContent>

                                                        {!isCheckOrConfirmationOrCancelOrder &&
                                                        !isCart ? (
                                                            <>
                                                                {showFooterNewsletter && (
                                                                    <FooterNewsletter />
                                                                )}
                                                                {!isConfiguratorActive && (
                                                                    <Footer
                                                                        roundedCorners={
                                                                            !showFooterNewsletter
                                                                        }
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <CheckoutFooter />
                                                        )}

                                                        <AuthModal {...authModal} />
                                                        <ExposedProductQuickviewModal />
                                                        <Toaster />
                                                    </Box>

                                                    {/* Keep at the end of the page to allow more critical resources to load faster */}
                                                    <CMSGlobalStyles />
                                                    <Gtm />
                                                    <CookieBot />
                                                    {/* dialogs have no dark design */}
                                                    <Chakra3Theme theme="light">
                                                        <OverlayViewport />
                                                    </Chakra3Theme>
                                                </ZendeskChatProvider>
                                            </NavigationProvider>
                                        </OverlayProvider>
                                    </AddToCartModalProvider>
                                </LeapOverlayProvider>

                                {PWA_DNT_NOTIFICATION_ENABLED && (
                                    <DntNotification {...dntNotification} />
                                )}

                                {!isCheckOrConfirmationOrCancelOrder && !isCart && geoIPData && (
                                    <CountrySelectorModal {...geoIpModalProps} />
                                )}
                            </HeaderProvider>
                        </CurrencyProvider>
                    </IntlProvider>
                </StorefrontPreview>
            </Box>
        </ConvertAPIProvider>
    )
}

App.shouldGetProps = () => false

App.propTypes = {
    children: PropTypes.node
}

export default App
